// Release notes shown in the What's New screen. Newest first.
//
// Each release is markdown per locale under content/<locale>/<id>.md, the same shape the help
// library uses, so wording can be corrected without touching Vue. Adding a release means adding
// one entry here and the two markdown files -- nothing else.
//
// `date` is ISO. It drives the displayed date and the "new" marker, so it must be the date the
// release reached clubs, not the date it was written.
//
// `icons` is one material icon per `##` section, in document order. Both locales are authored with
// the same sections in the same order, so the mapping holds without repeating it per language; a
// section past the end of the list simply renders without an icon.
const RELEASES = [
  {
    id: '2026-09',
    date: '2026-09-28',
    icons: [
      'sell',
      'event_busy',
      'how_to_reg',
      'storefront',
      'category',
      'bar_chart',
      'sensor_door',
      'tune',
      'info',
    ],
  },
  {
    id: '2026-08',
    date: '2026-08-24',
    icons: [
      'store',
      'schedule',
      'point_of_sale',
      'groups',
      'fitness_center',
      'inventory_2',
      'sensor_door',
      'admin_panel_settings',
      'info',
    ],
  },
];

// A release stays marked "new" for this long after its date, for anyone who never opens the screen.
const NEW_FOR_DAYS = 30;

const SEEN_STORAGE_KEY = 'fitnet.whatsNew.lastSeen';
const DISMISSED_ANNOUNCEMENT_STORAGE_KEY = 'fitnet.whatsNew.dismissedAnnouncement';

export default RELEASES;

export function latestRelease() {
  return RELEASES.length ? RELEASES[0] : null;
}

export function releaseById(id) {
  return RELEASES.find((release) => release.id === id) || null;
}

export function isRecent(release, now = new Date()) {
  if (!release || !release.date) return false;
  const published = new Date(`${release.date}T00:00:00Z`);
  if (Number.isNaN(published.getTime())) return false;
  return (now - published) / 86400000 <= NEW_FOR_DAYS;
}

// Reading the property itself throws where storage is blocked, so callers get null instead of a
// broken sidebar. The marker is then simply not remembered.
const storage = () => {
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

// Scoped per user: two people sharing a browser should not clear each other's marker.
const seenKey = (userId) => (userId ? `${SEEN_STORAGE_KEY}.${userId}` : SEEN_STORAGE_KEY);
const dismissedAnnouncementKey = (userId) => (
  userId
    ? `${DISMISSED_ANNOUNCEMENT_STORAGE_KEY}.${userId}`
    : DISMISSED_ANNOUNCEMENT_STORAGE_KEY
);

export function lastSeenReleaseId(userId) {
  const store = storage();
  if (!store) return '';
  try {
    return store.getItem(seenKey(userId)) || '';
  } catch (error) {
    return '';
  }
}

export function markLatestSeen(userId) {
  const store = storage();
  const latest = latestRelease();
  if (!store || !latest) return;
  try {
    store.setItem(seenKey(userId), latest.id);
  } catch (error) {
    // Storage may be disabled or full; the marker just shows again next time.
  }
}

export function dismissedAnnouncementReleaseId(userId) {
  const store = storage();
  if (!store) return '';
  try {
    return store.getItem(dismissedAnnouncementKey(userId)) || '';
  } catch (error) {
    return '';
  }
}

export function dismissLatestAnnouncement(userId) {
  const store = storage();
  const latest = latestRelease();
  if (!store || !latest) return;
  try {
    store.setItem(dismissedAnnouncementKey(userId), latest.id);
  } catch (error) {
    // Storage may be disabled or full; dismissal then lasts only for this mounted layout.
  }
}

// Unread means "has not opened the newest release", not "has not opened the screen" -- someone who
// read August should see the marker again in September.
export function hasUnreadRelease(userId) {
  const latest = latestRelease();
  if (!latest) return false;
  return lastSeenReleaseId(userId) !== latest.id;
}

export function shouldShowLatestAnnouncement(userId) {
  const latest = latestRelease();
  if (!latest || !hasUnreadRelease(userId)) return false;
  return dismissedAnnouncementReleaseId(userId) !== latest.id;
}

// Falls back to English so a release that has not been translated yet still renders, rather than
// showing an empty screen.
export async function loadReleaseBody(id, locale) {
  try {
    const mod = await import(`@/whats-new/content/${locale}/${id}.md`);
    return mod.default;
  } catch (e) {
    const fallback = await import(`@/whats-new/content/en/${id}.md`);
    return fallback.default;
  }
}
